import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarBlank, ArrowLeft, Phone, Tag, User, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-[#5C564D]">Loading article...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-3xl font-serif font-medium text-[#1C1914] mb-4">Article Not Found</h1>
          <p className="text-[#5C564D] mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Split content into paragraphs
  const paragraphs = post.content.split('\n\n').filter(p => p.trim());

  return (
    <div data-testid="blog-post-page">
      {/* Hero Image */}
      {post.image && (
        <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
          <img 
            src={post.image} 
            alt={post.image_alt || post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1914]/60 to-transparent" />
        </section>
      )}

      {/* Hero Section */}
      <section className={`py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0] ${post.image ? '-mt-24 relative z-10' : ''}`}>
        <div className={`max-w-4xl mx-auto ${post.image ? 'bg-[#F7F5F0] p-8 md:p-12 rounded-lg shadow-lg' : ''}`}>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#5C564D] hover:text-[#2C4C3B] font-medium mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>

          <div className="flex items-center gap-2 mb-4">
            <Tag weight="duotone" className="w-5 h-5 text-[#C5A059]" />
            <span className="text-sm font-medium uppercase tracking-wider text-[#C5A059]">
              {post.category}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-[#5C564D]">
            <span className="flex items-center gap-2">
              <User weight="duotone" className="w-5 h-5" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <CalendarBlank weight="duotone" className="w-5 h-5" />
              {new Date(post.published_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="prose">
                {paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {/* Keywords */}
              <div className="mt-12 pt-8 border-t border-[#E2DCD0]">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#5C564D] mb-4">Related Topics</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-[#F7F5F0] px-3 py-1 rounded-full text-sm text-[#5C564D]">
                    {post.target_keyword}
                  </span>
                  {post.secondary_keywords.map((keyword, index) => (
                    <span key={index} className="bg-[#F7F5F0] px-3 py-1 rounded-full text-sm text-[#5C564D]">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                {/* CTA Card */}
                <div className="bg-[#2C4C3B] p-6 rounded-lg text-white">
                  <h3 className="text-lg font-serif font-medium mb-3">Need Estate Sale Help?</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Contact us for a free consultation. We serve Fargo-Moorhead and surrounding communities.
                  </p>
                  <a
                    href="tel:2182982420"
                    className="flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-4 py-3 rounded-md transition-all duration-300 font-medium text-sm w-full"
                    data-testid="sidebar-phone"
                  >
                    <Phone weight="fill" className="w-4 h-4" />
                    218-298-2420
                  </a>
                </div>

                {/* Quick Links */}
                <div className="bg-[#F7F5F0] p-6 rounded-lg">
                  <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-4">Quick Links</h3>
                  <nav className="space-y-3">
                    <Link to="/faq" className="block text-[#5C564D] hover:text-[#2C4C3B] text-sm transition-colors">
                      Frequently Asked Questions
                    </Link>
                    <Link to="/service-areas" className="block text-[#5C564D] hover:text-[#2C4C3B] text-sm transition-colors">
                      Service Areas
                    </Link>
                    <Link to="/about" className="block text-[#5C564D] hover:text-[#2C4C3B] text-sm transition-colors">
                      About Us
                    </Link>
                    <Link to="/contact" className="block text-[#5C564D] hover:text-[#2C4C3B] text-sm transition-colors">
                      Contact
                    </Link>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-4">
            Ready to Learn More?
          </h2>
          <p className="text-[#5C564D] mb-8">
            Contact Legacy Liquidators LLC today for a free estate sale consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="post-cta-phone"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="post-cta-blog"
            >
              Read More Articles
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
